import React, { useContext, useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ThemeContext } from "../../constant/theme";
import { LanguageContext } from "../../constant/LanguageContext";

import WelcomeCard from "../../components/WelcomeCard";
import StatsGrid from "../../components/StatsGrid";
import * as icons from "../../constant/icons";
import { useEmployees } from "./hooks/useEmployees";
import { useEmployeeProfilePics } from "./hooks/useEmployeeProfilePics";
import EmptyCard from "../../components/EmptyCard";
import Button from "../../components/ConfigurableButton";
import AlertModal from "../../components/AlertModal";
import { Plus, Upload, Download, FileText, Archive, ShieldCheck } from "lucide-react";
import { getTopBarButtonStyle } from "../(Admin)Attendance/styles/attendanceStyles";
import AddEmployeeModal from "./components/modals/AddEmployeeModal";
import DocumentsModal from "./components/modals/DocumentsModal";
import ViewEmployeeModal from "./components/modals/ViewEmployeeModal";
import EditEmployeeModal from "./components/modals/EditEmployeeModal";
import {
  addEmployeeAPI,
  mapFormDataToDatabase,
  fetchEmployeeFullRecordAPI,
} from "./services/employeeService";
import { useEmployeeDocuments } from "./hooks/useEmployeeDocuments";
import { apiFetch, useAuth } from "../../context/AuthContext";
import ReusableDropdown, {
  unifiedControlStyle,
} from "../../components/ReusableDropdown";
import EmployeeTable from "./components/EmployeeTable";
import ImportPreviewModal from "./components/modals/ImportPreviewModal";
import { ImportResultsModal } from "./components/modals/ImportResultsModal";
import ImportProgressWidget from "./components/ImportProgressWidget";
import ImportErrorExplanation from "./components/modals/ImportErrorExplanation";
import { postImportRequest, explainImportRequestError } from "./utils/importIssues";
import { useEmployeeImportFlow } from "./hooks/useEmployeeImportFlow";
import { useEmployeeExport } from "./hooks/useEmployeeExport";
import { useArchivedEmployees } from "./hooks/useArchivedEmployees";
import { handleExportArchive as exportArchivedEmployeesAPI } from "./utils/employeeExport";
import ArchiveModal from "./components/modals/ArchiveModal";
import ExportTemplateModal from "../../components/ExportTemplateModal";
import HeadcountVerifyModal from "./components/modals/HeadcountVerifyModal";
import { useWorkingCompanyOptions } from "./hooks/useWorkingCompanyOptions";
import { SkeletonStyles, SkeletonBox, LoadingPill, SkeletonStatCard } from "../../components/SkeletonLoader";

import { text } from "./constants/text";
import CountWithSelf, { isSelfInList } from "../../components/CountWithSelf";
import { getResponsiveStyles } from "../../utils/styleUtils";
import { useProfile } from "../../hooks/useProfile";
import { getProfileFirstName } from "../../utils/profileIdentity";

// Import template files. Two variants of the same sheet: one where the user
// types Gross Salary (Net is a formula) and one where they type Net Salary
// (Gross is a formula). The Export Template dialog asks which one they want.
import templateFile from "../../Documents/Employees_template.xlsx";
import templateFileNet from "../../Documents/Employees_template_net.xlsx";
const Dashboard = ({
  employeeCount: propEmployeeCount,
  counts: propCounts,
  departmentStats: propDepartmentStats,
} = {}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();

  // derive a responsive screen size key for styles (safe on server)
  const screenSize =
    typeof window !== "undefined"
      ? window.innerWidth <= 480
        ? "xs"
        : window.innerWidth <= 768
          ? "sm"
          : window.innerWidth <= 1024
            ? "md"
            : window.innerWidth <= 1366
              ? "lg"
              : window.innerWidth <= 1920
                ? "xl"
                : "xxl"
      : "lg";

  const styles =
    typeof getResponsiveStyles === "function"
      ? getResponsiveStyles(screenSize, theme, language)
      : {};

  // derive values from props or hook (safe defaults)
  const {
    employeeCount: hookEmployeeCount,
    counts: hookCounts,

    fetchAll: refetchEmployees,
    companies,
    employees: hookEmployees,
    setEmployees,
    loading: employeesLoading,
    error: employeesError,
    clientsList,
  } = useEmployees();

  const { user, permissions: authPermissions, isColumnVisible } = useAuth();

  // The "Active Employees" / "On Leave" stat cards are derived from the
  // employeeState field, when an admin level has the Status column hidden,
  // that field never reaches this page, so the cards would show meaningless
  // zeros. Hide the cards themselves instead of showing wrong numbers.
  const isStatusColumnVisible = isColumnVisible("employees", "employeeTable", "status");

  const parseAdminLevel = () => {
    try {
      const raw = localStorage.getItem("adminLevel");
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn("Failed to parse adminLevel from storage:", err);
      return null;
    }
  };

  const normalizeAdminLevel = (rawAdminLevel) => {
    if (!rawAdminLevel || typeof rawAdminLevel !== "object") {
      return { modules: [], features: {} };
    }

    const modules = Array.isArray(rawAdminLevel.modules)
      ? rawAdminLevel.modules
        .map((item) => {
          if (typeof item === "string") return item;
          if (item?.module) return item.module;
          if (item?.moduleName) return item.moduleName;
          return null;
        })
        .filter(Boolean)
        .map((m) => String(m).trim().toLowerCase())
      : Array.isArray(rawAdminLevel.modulePermissions)
        ? rawAdminLevel.modulePermissions
          .map((mp) => mp?.module || mp?.moduleName)
          .filter(Boolean)
          .map((m) => String(m).trim().toLowerCase())
        : [];

    const normalizeFeatureValue = (value) => {
      const normalizeItem = (item) => String(item || "").trim().toLowerCase();

      if (Array.isArray(value)) {
        return value.map(normalizeItem).filter(Boolean);
      }
      if (value && typeof value === "object") {
        if (Array.isArray(value.features)) {
          return value.features.map(normalizeItem).filter(Boolean);
        }
        return Object.entries(value)
          .filter(([, enabled]) => enabled)
          .map(([featureName]) => normalizeItem(featureName))
          .filter(Boolean);
      }
      if (typeof value === "string") {
        return [normalizeItem(value)];
      }
      return [];
    };

    const features = {};
    if (rawAdminLevel.features && typeof rawAdminLevel.features === "object") {
      Object.entries(rawAdminLevel.features).forEach(([moduleName, value]) => {
        const normalized = normalizeFeatureValue(value);
        if (normalized.length) {
          const key = String(moduleName || "").trim().toLowerCase();
          features[key] = normalized;
        }
      });
    }

    if (!Array.isArray(features.employees) && Array.isArray(rawAdminLevel.modulePermissions)) {
      const employeeModule = rawAdminLevel.modulePermissions.find(
        (mp) => String(mp?.module || mp?.moduleName || "").trim().toLowerCase() === "employees"
      );
      if (employeeModule && Array.isArray(employeeModule.featurePermissions)) {
        features.employees = employeeModule.featurePermissions
          .filter((fp) => fp && fp.isEnabled !== false)
          .map((fp) =>
            String(fp.featureName || fp.feature || fp.name || "").trim().toLowerCase()
          )
          .filter(Boolean);
      }
    }

    return { ...rawAdminLevel, modules, features };
  };

  const [adminLevel, setAdminLevel] = useState(() => normalizeAdminLevel(authPermissions || parseAdminLevel()));

  // This component is the ADMIN VIEW of the employees page, always. It is
  // mounted in exactly two places and both gate on the admin view already:
  // User_Account renders AdminRoutes only under `viewMode === 'admin'`, and
  // Manager_Account's renderEmployees() returns it only under
  // `viewType === 'admin'` (otherwise it renders the manager page instead).
  //
  // This used to be read back out of
  // `localStorage.managerViewType || localStorage.viewType || 'manager'`,
  // which was actively harmful in two ways:
  //
  //  1. The two keys drift. User_Account's view switcher writes only
  //     `viewType`, while Manager_Account and the dashboard write both -- and
  //     `managerViewType` takes priority here. Worse, logout clears neither,
  //     so the value survives into the NEXT account signed in on the same
  //     browser.
  //  2. When the value read back as anything but "admin", `canViewEmployees`
  //     below short-circuited to true through its `!isAdminView` arm. The
  //     admin-level check was skipped entirely -- the page opened for an
  //     account whose admin level never granted the employees module.
  //
  // So the view is not read at all any more. The permission check below is
  // unconditional, and matches the scope the data fetches now pin (see the
  // ADMIN-VIEW SCOPING note in services/employeeService.js). An account with
  // no admin level gets Access Denied instead of the page, which is the
  // correct direction to fail.
  const isAdminRole = useMemo(() => {
    const role = (user?.role || profile?.role || localStorage.getItem("role") || "").toLowerCase();
    return role === "admin" || role === "superadmin";
  }, [user, profile]);

  // Headcount verification is for the real `admin` role ONLY -- not
  // superadmin, not promoted admin-level users. The server enforces the same
  // rule (allowedRoles('admin') on POST /employees/headcount/verify).
  const isAdminOnlyRole = useMemo(() => {
    const role = (user?.role || profile?.role || localStorage.getItem("role") || "").toLowerCase();
    return role === "admin";
  }, [user, profile]);
  const [showHeadcountVerify, setShowHeadcountVerify] = useState(false);

  const employeeModules = useMemo(
    () => (Array.isArray(adminLevel?.modules) ? adminLevel.modules : []),
    [adminLevel],
  );
  const employeeFeatures = useMemo(
    () =>
      Array.isArray(adminLevel?.features?.employees)
        ? adminLevel.features.employees
        : [],
    [adminLevel],
  );

  const hasEmployeeFeature = (feature) => {
    // Real admin/superadmin accounts hold every feature implicitly; everyone
    // else must have it granted on their admin level.
    if (isAdminRole) return true;
    if (!employeeModules.includes("employees")) return false;

    const normalize = (value) => String(value || "").trim().toLowerCase();
    const normalizedFeature = normalize(feature);
    const normalizedEmployeeFeatures = employeeFeatures.map(normalize);

    const featureAliases = {
      delete: ["remove"],
      create: ["add"],
      remove: ["delete"],
      add: ["create"],
      exporttemplate: ["export_template", "export-template"],
      export_template: ["exporttemplate", "export-template"],
      "export-template": ["exporttemplate", "export_template"],
      viewdocuments: ["documents", "document", "view_document", "view_documents"],
      documents: ["viewdocuments", "document", "view_document", "view_documents"],
      document: ["viewdocuments", "documents", "view_document", "view_documents"],
      viewdocument: ["viewdocuments", "documents", "document", "view_document", "view_documents"],
      view_archived: ["viewarchived", "archived", "archive"],
      viewarchived: ["view_archived", "archived", "archive"],
      archived: ["viewarchived", "view_archived", "archive"],
      archive: ["viewarchived", "view_archived", "archived"],
    };

    const aliasKeys = featureAliases[normalizedFeature] || [];
    const featureKeys = [normalizedFeature, ...aliasKeys].map(normalize);
    return featureKeys.some((key) => normalizedEmployeeFeatures.includes(key));
  };

  // Page-level check, not hasEmployeeFeature("view"), the "employees" page
  // in the permission registry has no "view" feature key at all (only
  // add/edit/remove/export/...), so granting "Full Access" in Admin Level
  // Configuration only ever populates adminLevel.modules with "employees",
  // never a features.employees "view" entry. Gating on hasEmployeeFeature
  // ("view") meant Full Access could never actually unlock this page for a
  // promoted user, it always fell through to the Access Denied screen.
  // No view-type escape hatch: see the admin-view note above. Access requires
  // a real admin role, or the "employees" module on the account's admin level.
  const canViewEmployees = isAdminRole || employeeModules.includes("employees");
  const canCreateEmployee = hasEmployeeFeature("add");
  const canEditEmployee = hasEmployeeFeature("edit");
  const canDeleteEmployee = hasEmployeeFeature("remove");
  const canExportEmployees = hasEmployeeFeature("export");
  const canImportEmployees = hasEmployeeFeature("import");
  const canViewArchivedEmployees = hasEmployeeFeature("viewArchived");
  const canRestoreArchived = hasEmployeeFeature("restoreArchived");
  const canDeleteArchivedEmployee = hasEmployeeFeature("deleteArchived");
  const canExportTemplate = hasEmployeeFeature("exportTemplate");
  const canViewDocuments = hasEmployeeFeature("viewDocuments");

  const hasAdminActionButtons =
    canCreateEmployee ||
    canImportEmployees ||
    canExportEmployees ||
    canViewArchivedEmployees ||
    canExportTemplate ||
    isAdminOnlyRole;

  useEffect(() => {
    const syncAdminLevel = () => {
      setAdminLevel(normalizeAdminLevel(authPermissions || parseAdminLevel()));
    };

    syncAdminLevel();
    window.addEventListener("storage", syncAdminLevel);
    return () => window.removeEventListener("storage", syncAdminLevel);
  }, [authPermissions]);

  const employeeModulesKey = useMemo(
    () => employeeModules.join(","),
    [employeeModules],
  );
  const employeeFeaturesKey = useMemo(
    () => JSON.stringify(employeeFeatures),
    [employeeFeatures],
  );

  // Refetch when the admin level CHANGES -- a permission edit widens or
  // narrows what this account may see, so the cached list is stale.
  //
  // It deliberately does not fire on mount, and `refetchEmployees` is
  // deliberately not a dependency. useEmployees already runs its own fetch on
  // mount and again whenever fetchAll's identity changes, so having this
  // effect do the same meant every page open fired the load twice: the second
  // call aborted the first mid-flight. With `clientName` resolving
  // asynchronously from useProfile that became four requests and three aborts
  // per open, and which of them won the race decided whether the page painted
  // its data or sat there empty.
  const lastAdminLevelKey = useRef(null);
  useEffect(() => {
    const key = `${adminLevel?.adminLevelId ?? ""}|${employeeModulesKey}|${employeeFeaturesKey}`;
    const previous = lastAdminLevelKey.current;
    lastAdminLevelKey.current = key;
    // First pass just records the baseline -- useEmployees owns the initial load.
    if (previous === null || previous === key) return;
    if (typeof refetchEmployees === "function") refetchEmployees(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminLevel?.adminLevelId, employeeModulesKey, employeeFeaturesKey]);

  // Get employee profile pictures
  const { employeeProfilePics, fetchProfilePic, setEmployeeProfilePics } = useEmployeeProfilePics(hookEmployees);

  // Memoize unique department names derived from employees (match EmployeeTable.js behavior)
  const departmentsList = useMemo(() => {
    try {
      const list = Array.isArray(hookEmployees)
        ? hookEmployees.map((e) => e?.department || "").filter(Boolean)
        : [];
      return Array.from(new Set(list)).sort();
    } catch (err) {
      return [];
    }
  }, [hookEmployees]);


  // ─── Skeleton فقط في أول تحميل, مش هيظهر تاني مع كل ريفريش ─────────────────
  const [pageReady, setPageReady] = useState(false);
  useEffect(() => {
    if (!employeesLoading) setPageReady(true);
  }, [employeesLoading]);

  // Stat-card numbers. These come straight from useEmployees, which derives
  // every one of them from the visibility-scoped payloads this page already
  // renders -- so a card can never report employees the current admin level
  // isn't allowed to see.
  //
  // The `propCounts` / `propEmployeeCount` props are still honoured for any
  // caller that wants to drive the cards itself, but nothing in the app passes
  // them today; when absent, the derived values are used as-is. The old
  // "backend count, fall back to visible count" merge that used to live here
  // is gone -- the backend counts it preferred were client-wide totals.
  const counts = {
    ...hookCounts,
    ...(propCounts || {}),
  };
  const employeeCount = propEmployeeCount ?? hookEmployeeCount ?? counts.Total ?? 0;

  // Import flow state
  const {
    pendingImportFile,
    setPendingImportFile,
    importPreviewOpen,
    setImportPreviewOpen,
    importPreviewData,
    setImportPreviewData,
    duplicateDecisions,
    setDuplicateDecisions,
    managerResolutions,
    setManagerResolutions,
    previewStep,
    setPreviewStep,
    expandedDupKeys,
    setExpandedDupKeys,
    isPreparingImport,
    setIsPreparingImport,
    isImportCommitting,
    setIsImportCommitting,
    importProgress,
    setImportProgress,
    importResultsOpen,
    setImportResultsOpen,
    importResultsData,
    setImportResultsData,
    importMode,
  } = useEmployeeImportFlow();

  // Export flow state
  const { isExporting, handleExport } = useEmployeeExport();
  const [filteredEmployeesForExport, setFilteredEmployeesForExport] = useState([]);
  const [importQuotaStatus, setImportQuotaStatus] = useState(null);
  const [importQuotaLoading, setImportQuotaLoading] = useState(false);

  // Pulls the client's quota/config status. AddEmployeeModal reads
  // `allowImportModeChoice` from it to decide whether to offer "Add As".
  // Fetched fresh every time the form opens since it may have changed.
  const fetchImportQuotaStatus = async () => {
    const clientName = profile?.clientName;
    if (!clientName) return;
    try {
      setImportQuotaLoading(true);
      const response = await apiFetch(
        `${BASE_URL}/employees/client/${encodeURIComponent(clientName)}/quota-status`,
        { method: "GET" }
      );
      const result = await response.json();
      if (response.ok && result?.success) {
        setImportQuotaStatus(result.data);
      }
    } catch (err) {
      console.error("Failed to fetch import quota status:", err);
    } finally {
      setImportQuotaLoading(false);
    }
  };

  // Archive flow state
  const {
    archivedEmployees,
    archivedCount,
    loading: archiveLoading,
    archiveSearch,
    setArchiveSearch,
    archiveFilterCompany,
    setArchiveFilterCompany,
    archiveFilterCompanySearch,
    setArchiveFilterCompanySearch,
    archiveFilterWorkingCompany,
    setArchiveFilterWorkingCompany,
    archiveFilterWorkingCompanySearch,
    setArchiveFilterWorkingCompanySearch,
    archiveFilterDepartment,
    setArchiveFilterDepartment,
    archiveFilterDepartmentSearch,
    setArchiveFilterDepartmentSearch,
    archiveFilterDateFrom,
    setArchiveFilterDateFrom,
    archiveFilterDateTo,
    setArchiveFilterDateTo,
    filteredArchivedEmployees,
    filterOptions,
    archivedThisMonth,
    fetchArchivedEmployees,
    restoreEmployee,
    restoreAllEmployees,
    deleteAllArchived,
    deleteArchivedEmployee,
    clearFilters: clearArchiveFilters,
  } = useArchivedEmployees();

  const [showArchive, setShowArchive] = useState(false);
  const [showViewEmployee, setShowViewEmployee] = useState(false);
  const [selectedEmployeeForView, setSelectedEmployeeForView] = useState(null);

  const fileInputRef = useRef(null);

  // Documents modal state
  const {
    showDocumentsModal,
    setShowDocumentsModal,
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,
    loadingDocuments,
    savingDocument,
    hasUnsavedChanges,
    documentChecklist,
    loadEmployeeDocuments,
    updateDocumentCheckbox,
    saveDocuments,
  } = useEmployeeDocuments();

  // Add employee modal state
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    title: "",
    message: "",
    variant: "info",
    showCancel: false,
  });

  // Edit employee modal state
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Working companies of the client, for the headcount "specific company" choice.
  const { companyOptions: headcountCompanyOptions } = useWorkingCompanyOptions({ companies });

  // Export template modal state
  const [showExportTemplate, setShowExportTemplate] = useState(false);
  const [exportTemplateLoading, setExportTemplateLoading] = useState(false);

  const showAlert = ({ message, title, variant = "info" }) =>
    new Promise((resolve) => {
      setAlertState({
        open: true,
        title: title || "",
        message,
        variant,
        showCancel: false,
        onResolve: resolve,
      });
    });

  const showConfirm = ({ message, title = "", variant = "error" } = {}) =>
    new Promise((resolve) => {
      setAlertState({
        open: true,
        title,
        message,
        variant,
        showCancel: true,
        onResolve: resolve,
      });
    });

  // An import that couldn't start or finish: say what happened and how to fix it.
  const showImportError = (err) => {
    const isAr = language === "ar";
    const explanation = explainImportRequestError(err, isAr);
    return showAlert({
      title: explanation.title,
      message: <ImportErrorExplanation explanation={explanation} isRtl={isAr} />,
      variant: "error",
    });
  };

  const handleMinimizeImportProgress = () =>
    setImportProgress((prev) => ({ ...prev, minimized: true }));
  const handleExpandImportProgress = () =>
    setImportProgress((prev) => ({ ...prev, minimized: false }));
  const handleCloseImportProgress = () =>
    setImportProgress((prev) => ({ ...prev, visible: false }));

  const closeImportPreview = () => {
    setImportPreviewOpen(false);
    setPendingImportFile(null);
    setImportPreviewData({
      duplicates: [],
      failed: [],
      parseWarnings: [],
      managerWarnings: [],
      salaryRejections: [],
      summary: {},
    });
    setDuplicateDecisions({});
    setManagerResolutions({});
    setPreviewStep(1);
    setExpandedDupKeys({});
  };

  // Per-row time estimate used only to drive the progress widget's bar/ETA,
  // the backend has no real progress events, so this is a rough guess based
  // on row count from the preview step, clamped to a sane range.
  const IMPORT_MS_PER_ROW = 120;
  const IMPORT_MIN_ESTIMATE_MS = 3000;
  const IMPORT_MAX_ESTIMATE_MS = 5 * 60 * 1000;

  const commitImportWithDecisions = async (file, decisions = {}, managerResolutions = {}, previewSummary = null) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName ?? "");
    formData.append("mode", "commit");
    formData.append("importMode", importMode);
    formData.append("duplicateDecisions", JSON.stringify(decisions));
    formData.append("managerResolutions", JSON.stringify(managerResolutions));

    // Prefer the summary passed explicitly (the auto-commit path below calls
    // this synchronously right after setImportPreviewData, before React has
    // re-rendered, so the importPreviewData closure below would be stale).
    const summary = previewSummary || importPreviewData?.summary || {};
    const estimatedRows =
      (summary.toCreate || 0) +
      (summary.toUpdate || 0) +
      (summary.skipped || 0) +
      (summary.failed || 0);
    const estimatedTotalMs = Math.min(
      IMPORT_MAX_ESTIMATE_MS,
      Math.max(IMPORT_MIN_ESTIMATE_MS, (estimatedRows || 40) * IMPORT_MS_PER_ROW),
    );

    setIsImportCommitting(true);
    setLoading(true);
    setImportProgress({
      visible: true,
      minimized: false,
      percent: 0,
      etaSeconds: estimatedTotalMs / 1000,
      fileName: file?.name || "",
    });

    const startedAt = Date.now();
    const progressTimer = setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const percent = Math.min(95, (elapsedMs / estimatedTotalMs) * 100);
      const etaSeconds = Math.max(0, (estimatedTotalMs - elapsedMs) / 1000);
      setImportProgress((prev) =>
        prev.visible ? { ...prev, percent, etaSeconds } : prev,
      );
    }, 250);

    try {
      const result = await postImportRequest(apiFetch, `${BASE_URL}/employees/import-excel`, formData);
      closeImportPreview();
      setImportResultsData(result);
      setImportResultsOpen(true);
      // Import just changed the dataset on the server, force a real refetch.
      if (typeof refetchEmployees === "function") refetchEmployees(true);
    } catch (err) {
      await showImportError(err);
    } finally {
      clearInterval(progressTimer);
      setImportProgress((prev) => ({ ...prev, percent: 100, etaSeconds: 0 }));
      setTimeout(() => {
        setImportProgress((prev) => ({ ...prev, visible: false }));
      }, 1200);
      setIsImportCommitting(false);
      setLoading(false);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const inputElement = event.target;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clientName", profile?.clientName ?? "");
    formData.append("mode", "preview");
    formData.append("importMode", importMode);
    try {
      setIsPreparingImport(true);
      setLoading(true);
      const result = await postImportRequest(apiFetch, `${BASE_URL}/employees/import-excel`, formData);
      const previewDuplicates = Array.isArray(result.duplicates)
        ? result.duplicates
        : [];
      const previewFailed = Array.isArray(result.failed) ? result.failed : [];
      const previewParseWarnings = Array.isArray(result.parseWarnings)
        ? result.parseWarnings
        : [];
      const previewManagerWarnings = Array.isArray(result.managerWarnings)
        ? result.managerWarnings
        : [];
      const previewSalaryRejections = Array.isArray(result.salaryRejections)
        ? result.salaryRejections
        : [];
      setPendingImportFile(file);
      setImportPreviewData({
        duplicates: previewDuplicates,
        failed: previewFailed,
        parseWarnings: previewParseWarnings,
        managerWarnings: previewManagerWarnings,
        salaryRejections: previewSalaryRejections,
        summary: result.summary || {},
      });
      // Any row that won't import (missing fields, repeated code, quota, ...)
      // opens the preview so the user sees why before committing.
      const hasValidationErrors = previewFailed.length > 0;
      if (
        previewDuplicates.length > 0 ||
        hasValidationErrors ||
        previewParseWarnings.length > 0 ||
        previewManagerWarnings.length > 0 ||
        previewSalaryRejections.length > 0
      ) {
        setDuplicateDecisions({});
        setManagerResolutions({});
        setExpandedDupKeys({});
        setPreviewStep(
          hasValidationErrors ||
            previewParseWarnings.length > 0 ||
            previewManagerWarnings.length > 0 ||
            previewSalaryRejections.length > 0
            ? 1
            : 2,
        );
        setImportPreviewOpen(true);
      } else {
        await commitImportWithDecisions(file, {}, {}, result.summary || {});
      }
    } catch (err) {
      console.error("Import error:", err);
      await showImportError(err);
    } finally {
      setIsPreparingImport(false);
      setLoading(false);
      if (inputElement) inputElement.value = "";
    }
  };

  const handleOpenAdd = () => {
    setShowAddEmployee(true);
    // The single-add form needs the same superadmin switch the bulk import
    // reads (`allowImportModeChoice`) to decide whether to offer the
    // "Add As" choice at all, so refresh the quota/config status on open.
    fetchImportQuotaStatus();
  };

  const handleExportToExcel = async () => {
    try {
      // Get current filter states from the EmployeeTable
      const filters = {
        searchTerm: "", // Will be populated from EmployeeTable filters if needed
        filterDepartment: "",
        filterStatus: "",
        filterCompany: "",
      };

      // Prefer filtered employees supplied by the table; fall back to full hookEmployees
      const employeesToExport =
        Array.isArray(filteredEmployeesForExport) && filteredEmployeesForExport.length > 0
          ? filteredEmployeesForExport
          : Array.isArray(hookEmployees)
            ? hookEmployees
            : [];

      if (employeesToExport.length === 0) {
        await showAlert({
          title: text[language].export,
          message:
            text[language].noEmployeesToExport || "No employees to export",
          variant: "info",
        });
        return;
      }

      // Perform export
      await handleExport(employeesToExport, filters, {
        onSuccess: async (result) => {
          const format = result.format === "excel" ? "Excel" : "CSV";
          await showAlert({
            title: text[language].export,
            message: `${text[language].exportSuccess || "Successfully exported"} ${result.count} ${text[language].employees || "employees"} ${language === "ar" ? "إلى" : "to"} ${format}`,
            variant: "success",
          });
        },
        onError: async (err) => {
          await showAlert({
            title: text[language].export,
            message: `${text[language].failedToExport || "Failed to export"}: ${err.message}`,
            variant: "error",
          });
        },
      });
    } catch (err) {
      console.error("Export error:", err);
      await showAlert({
        title: text[language].export,
        message:
          err.message ||
          text[language].failedToExport ||
          "Failed to export employees",
        variant: "error",
      });
    }
  };

  // Archive handlers
  const getStatusBadge = (status) => {
    // Base styles for all status badges
    const baseStyle = {
      padding: screenSize === "xs" ? "0.25rem 0.5rem" : "0.25rem 0.625rem",
      borderRadius: screenSize === "xs" ? "0.5rem" : "0.75rem",
      fontSize: "0.875rem",
      fontWeight: "600",
    };

    // Return styled span based on status
    switch (status) {
      case "Active":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#d1fae5",
              color: "#065f46",
            }}
          >
            {text[language].statusActive}
          </span>
        );
      case "OnLeave":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fef3c7",
              color: "#92400e",
            }}
          >
            {text[language].statusOnLeave}
          </span>
        );
      case "Inactive":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#fee2e2",
              color: "#991b1b",
            }}
          >
            {text[language].statusInactive}
          </span>
        );
      case "Archived":
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#6b7280",
            }}
          >
            {text[language].statusArchived}
          </span>
        );
      default:
        return (
          <span
            style={{
              ...baseStyle,
              backgroundColor: "#e5e7eb",
              color: "#374151",
            }}
          >
            {text[language].statusUnknown}
          </span>
        );
    }
  };

  const handleOpenArchive = async () => {
    setShowArchive(true);
    await fetchArchivedEmployees();
  };

  const handleOpenExportTemplate = async () => {
    setShowExportTemplate(true);
  };

  const handleExportTemplate = async (salaryMode = "gross") => {
    try {
      setExportTemplateLoading(true);

      // Fetch the actual template file (the imports are webpack-processed URLs)
      const response = await fetch(salaryMode === "net" ? templateFileNet : templateFile);

      if (!response.ok) {
        throw new Error("Failed to load template file");
      }

      const blob = await response.blob();
      return blob;
    } catch (err) {
      console.error("Failed to export template:", err);
      await showAlert({
        title: text[language].exportTemplate,
        message:
          err.message ||
          text[language].failedToExport ||
          "Failed to export template",
        variant: "error",
      });
      throw err;
    } finally {
      setExportTemplateLoading(false);
    }
  };

  const handleRestoreEmployee = async (employeeId) => {
    const confirmed = await showConfirm({
      title: text[language].confirmRestoree || text[language].restore,
      message:
        text[language].confirmRestore ||
        "Are you sure you want to restore this employee?",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await restoreEmployee(employeeId);

      if (result.success) {
        await showAlert({
          title: text[language].restoreEmployee,
          message:
            text[language].employeeRestored || "Employee restored successfully",
          variant: "success",
        });
        // Refresh main employee list, force, since the restore mutated data.
        if (typeof refetchEmployees === "function") refetchEmployees(true);
      } else {
        throw new Error(result.error || "Failed to restore employee");
      }
    } catch (err) {
      await showAlert({
        title: text[language].restoreEmployee || "Restore",
        message:
          err.message ||
          text[language].failedToRestore ||
          "Failed to restore employee",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreAllArchivedEmployees = async () => {
    const confirmed = await showConfirm({
      title: text[language].confirmRestoree || text[language].restoreAll,
      message: `${text[language].confirmRestoreAll || "Are you sure you want to restore all"} ${archivedCount} ${text[language].archivedEmployeesQuestion || "archived employees?"}`,
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await restoreAllEmployees();

      await showAlert({
        title: text[language].restoreAll,
        message: `${text[language].restored || "Restored"} ${result.successCount} ${text[language].employees || "employees"}${result.failCount > 0 ? `. ${result.failCount} failed.` : ""}`,
        variant: result.failCount > 0 ? "warning" : "success",
      });

      // Refresh archived list and main employee list, force, since
      // restoring all archived employees mutated both datasets.
      if (typeof fetchArchivedEmployees === "function") {
        try { await fetchArchivedEmployees(true); } catch (e) { /* ignore */ }
      }
      if (typeof refetchEmployees === "function") {
        try { await refetchEmployees(true); } catch (e) { /* ignore */ }
      }

      // If nothing was restored, show error hint
      if (result.successCount === 0 && result.failCount > 0) {
        await showAlert({
          title: text[language].restoreAll || "Restore All",
          message: text[language].failedToRestore || "Failed to restore employees",
          variant: "error",
        });
      }
    } catch (err) {
      await showAlert({
        title: text[language].restoreAll || "Restore All",
        message:
          err.message ||
          text[language].failedToRestore ||
          "Failed to restore employees",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllArchived = async () => {
    const confirmed = await showConfirm({
      title: text[language].deleteAll || "Delete All",
      message: `${text[language].confirmDeleteAll || "Are you sure you want to permanently delete all"} ${archivedCount} ${text[language].archivedEmployeesQuestion || "archived employees?"}${text[language].cannotBeUndone || "This action cannot be undone."}`,
      variant: "error",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await deleteAllArchived();

      if (result.success) {
        await showAlert({
          title: text[language].deleteAll,
          message:
            text[language].allEmployeesDeleted ||
            "All archived employees permanently deleted!",
          variant: "success",
        });
      } else {
        throw new Error(result.error || "Failed to delete archived employees");
      }
    } catch (err) {
      await showAlert({
        title: text[language].deleteAll || "Delete All",
        message: err.message || "Failed to delete archived employees",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArchivedEmployee = async (employeeId) => {
    const confirmed = await showConfirm({
      title: text[language].deleteEmployee || "Delete",
      message:
        text[language].confirmDeleteArchived ||
        "Are you sure you want to permanently delete this archived employee? This action cannot be undone.",
      variant: "error",
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      const result = await deleteArchivedEmployee(employeeId);

      if (result.success) {
        await showAlert({
          title: text[language].deleteEmployee || "Delete",
          message:
            text[language].employeeDeleted ||
            "Archived employee permanently deleted!",
          variant: "success",
        });
      } else {
        throw new Error(result.error || "Failed to delete archived employee");
      }
    } catch (err) {
      await showAlert({
        title: text[language].deleteEmployee || "Delete",
        message: err.message || "Failed to delete archived employee",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportArchive = async () => {
    // Use the count of currently filtered archived employees just to decide
    // whether there's anything to export; the actual export is performed
    // server-side against the archived-employees table using the same
    // filters, so it always reflects the real archived records.
    if (filteredArchivedEmployees.length === 0) {
      await showAlert({
        title: text[language].exportArchive || "Export Archive",
        message:
          text[language].noEmployeesToExport || "No employees to export",
        variant: "info",
      });
      return;
    }

    try {
      // Hit the dedicated archived-employees export endpoint (queries the
      // ArchivedEmployee table) instead of the generic active-employee
      // export, which looked employees up by infinityCode in the active
      // Employee table and therefore never found (or filtered) archived ones.
      await exportArchivedEmployeesAPI({
        filters: {
          searchTerm: archiveSearch,
          filterCompany: archiveFilterCompany,
          filterWorkingCompany: archiveFilterWorkingCompany,
          filterDepartment: archiveFilterDepartment,
          dateFrom: archiveFilterDateFrom,
          dateTo: archiveFilterDateTo,
        },
        showAlert,
        text,
        language,
      });
    } catch (err) {
      // exportArchivedEmployeesAPI already surfaces an alert on failure.
      console.error("Export archive error:", err);
    }
  };

  // Helper function to get employee initials
  const getUserInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  // Action handlers for employee table
  // The list endpoint returns only the columns the table renders
  // (?fields=table), so `fullEmployeeData` on a row is a partial record.
  // The view/edit/documents modals need every column -- fetch it for the ONE
  // employee being opened and merge it in. Falls back to the partial row if
  // the request fails, so a network hiccup degrades rather than breaks.
  const hydrateFullEmployee = useCallback(async (employee) => {
    const code = employee?.fullEmployeeData?.infinityCode || employee?.id;
    if (!code) return employee;
    try {
      const full = await fetchEmployeeFullRecordAPI(code);
      if (!full) return employee;
      return {
        ...employee,
        fullEmployeeData: { ...(employee.fullEmployeeData || {}), ...full },
      };
    } catch {
      return employee;
    }
  }, []);

  const handleViewEmployee = (employee) => {
    // Show the modal immediately with what we already have, then fill in the
    // remaining fields when they arrive -- avoids a blocking spinner.
    setSelectedEmployeeForView(employee);
    setShowViewEmployee(true);
    hydrateFullEmployee(employee).then((hydrated) => {
      setSelectedEmployeeForView((cur) =>
        cur && cur.id === hydrated.id ? hydrated : cur,
      );
    });

    const code = employee?.id;
    if (code && !employeeProfilePics[code]) {
      fetchProfilePic(code).then((url) => {
        if (url) {
          setEmployeeProfilePics((prev) => (prev[code] ? prev : { ...prev, [code]: url }));
        }
      });
    }
  };

  // Notification deep-link (?employee=<infinityCode>): auto-open that
  // employee's profile view once the list is loaded (emp.id IS the
  // infinityCode, see employeeFormatters).
  const [searchParams] = useSearchParams();
  const deepLinkConsumedRef = useRef(false);
  useEffect(() => {
    const code = searchParams.get("employee");
    if (!code || deepLinkConsumedRef.current) return;
    const list = Array.isArray(hookEmployees) ? hookEmployees : [];
    const employee = list.find((e) => String(e?.id) === String(code));
    if (employee) {
      deepLinkConsumedRef.current = true;
      handleViewEmployee(employee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookEmployees, searchParams]);

  // ── Headcount modal → page modals ─────────────────────────────────────────
  // The headcount modal can open this page's own Edit / View / Add modals.
  // It is hidden (not unmounted, so its results survive) while one of them is
  // open, then re-verifies the same file when they close.
  const [headcountSuspended, setHeadcountSuspended] = useState(false);
  const [headcountRefresh, setHeadcountRefresh] = useState(0);
  const findEmployeeForHeadcount = (infinityCode) => {
    const list = Array.isArray(hookEmployees) ? hookEmployees : [];
    return list.find((e) => String(e?.id) === String(infinityCode)) || { id: infinityCode };
  };
  const handleHeadcountEdit = (infinityCode) => {
    setHeadcountSuspended(true);
    handleEditEmployee(findEmployeeForHeadcount(infinityCode));
  };
  const handleHeadcountView = (infinityCode) => {
    setHeadcountSuspended(true);
    handleViewEmployee(findEmployeeForHeadcount(infinityCode));
  };
  const handleHeadcountAdd = () => {
    setHeadcountSuspended(true);
    handleOpenAdd();
  };
  useEffect(() => {
    if (headcountSuspended && !showEditEmployee && !showViewEmployee && !showAddEmployee) {
      setHeadcountSuspended(false);
      setHeadcountRefresh((n) => n + 1);
    }
  }, [headcountSuspended, showEditEmployee, showViewEmployee, showAddEmployee]);

  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEditEmployee(true);
    // Edit needs the complete record (the form spreads fullEmployeeData into
    // its defaults), so hydrate before the user starts changing fields.
    hydrateFullEmployee(employee).then((hydrated) => {
      setSelectedEmployee((cur) =>
        cur && cur.id === hydrated.id ? hydrated : cur,
      );
    });
  };

  const handleUpdateEmployee = async (employeeData, uploadedFiles = []) => {
    try {
      setLoading(true);

    // The edit modal submits the COMPLETE field set already keyed by the
    // backend field names; the backend whitelists and type-coerces every
    // schema field, so the payload is passed through as-is.
    //
    // clientName is deliberately NEVER included here, it must be
    // completely immutable outside the dedicated superadmin "rename client"
    // operation (see configurationService.renameClient), which cascades a
    // rename to every employee under a client at once rather than letting
    // it drift per-record through an edit form. The backend independently
    // strips clientName from every update payload too
    // (employeeService.coerceEmployeeUpdateData), but it's excluded here as
    // well so there's no path, frontend or backend, that can change it.
    // eslint-disable-next-line no-unused-vars
    const { clientName: _ignoredClientName, ...employeeDataWithoutClientName } = employeeData;
    const payload = { ...employeeDataWithoutClientName };

      // If files are attached, use FormData to send both JSON payload and files
      let response;
      if (uploadedFiles && uploadedFiles.length > 0) {
        const formData = new FormData();
        // Append the employee JSON payload as a string field
        formData.append("data", JSON.stringify(payload));
        uploadedFiles.forEach((file) => {
          formData.append("documents", file, file.name);
        });

        response = await apiFetch(
          `${BASE_URL}/employees/${selectedEmployee.id}`,
          {
            method: "PUT",
            body: formData,
            // Do NOT set Content-Type header, browser sets it with boundary for FormData
          },
        );
      } else {
        response = await apiFetch(
          `${BASE_URL}/employees/${selectedEmployee.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
      }

      const responseData = await response.json();
      if (!response.ok || !responseData?.success) {
        throw new Error(
          responseData?.error ||
          responseData?.message ||
          "Failed to update employee",
        );
      }

      const updated = responseData.employee || {};
      setEmployees((prev) =>
        prev.map((emp) =>
          emp.id === selectedEmployee.id
            ? {
              ...emp,
              name: updated.sr || emp.name,
              email: updated.email || emp.email,
              phone: updated.phoneNumber || emp.phone,
              department: updated.department?.name ?? emp.department,
              departmentId: updated.departmentId ?? emp.departmentId,
              position: updated.position ?? emp.position,
              laborMode: updated.laborMode || emp.laborMode,
              company: updated.clientName || emp.company,
              status: updated.employeeState || emp.status,
              salary: updated.netSalary ?? emp.salary,
              arabicName: updated.arabicName || emp.arabicName,
              accountNumber: updated.accountNumber ?? emp.accountNumber,
              bankName: updated.bankName ?? emp.bankName,
              fullEmployeeData: {
                ...(emp.fullEmployeeData || {}),
                ...updated,
              },
            }
            : emp,
        ),
      );

      setShowEditEmployee(false);
      setSelectedEmployee(null);

      await showAlert({
        title: language === "ar" ? "تم التحديث بنجاح" : "Update Success",
        message:
          language === "ar"
            ? `تم تحديث بيانات الموظف بنجاح${uploadedFiles.length > 0 ? ` وتم رفع ${uploadedFiles.length} مستند` : ""}`
            : `Employee updated successfully${uploadedFiles.length > 0 ? ` and ${uploadedFiles.length} document${uploadedFiles.length > 1 ? "s" : ""} uploaded` : ""}`,
        variant: "success",
      });

      // Update mutated the employee record, force a real refetch.
      if (typeof refetchEmployees === "function") refetchEmployees(true);
    } catch (err) {
      console.error("Failed to update employee:", err);
      await showAlert({
        title: language === "ar" ? "فشل التحديث" : "Update Failed",
        message:
          err.message ||
          (language === "ar"
            ? "فشل تحديث الموظف"
            : "Failed to update employee"),
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = (empOrId) => {
    (async () => {
      // Accept either an employee object or a bare ID
      const id = empOrId && typeof empOrId === "object" ? empOrId.id : empOrId;
      try {
        const confirmed = await showConfirm({
          title: text[language].deleteEmployee || "Delete",
          message:
            text[language].confirmDelete ||
            "Are you sure you want to delete this employee?",
          variant: "error",
        });
        if (!confirmed) return;
        setLoading(true);
        const response = await apiFetch(`${BASE_URL}/employees/${id}`, {
          method: "DELETE",
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            data?.error || data?.message || "Failed to delete employee",
          );
        }
        // close edit modal if it was open for this employee
        setShowEditEmployee(false);
        setSelectedEmployee(null);
        await showAlert({
          title: text[language].deleteEmployee,
          message:
            text[language].employeeDeleted || "Employee deleted successfully",
          variant: "success",
        });
        // Reload data from server to reflect deletion, force, bypass cache.
        if (typeof refetchEmployees === "function") refetchEmployees(true);
      } catch (err) {
        console.error("Failed to delete employee:", err);
        await showAlert({
          title: text[language].deleteEmployee || "Delete",
          message:
            err.message ||
            (language === "ar"
              ? "فشل حذف الموظف"
              : "Failed to delete employee"),
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleViewDocuments = async (employee) => {
    // The hook loads the saved checklist itself; if that fails the modal
    // stays closed instead of showing unticked boxes that a save would keep.
    try {
      await loadEmployeeDocuments(employee);
    } catch (err) {
      await showAlert({
        message:
          language === "ar"
            ? "تعذر تحميل قائمة مستندات الموظف"
            : "Could not load this employee's documents checklist",
        variant: "error",
      });
    }
  };

  const handleSaveDocuments = async () => {
    if (!selectedEmployeeDocuments?.id) return;
    try {
      await saveDocuments(selectedEmployeeDocuments.id, setEmployees);
      await showAlert({
        message: language === "ar" ? "تم الحفظ بنجاح" : "Saved successfully",
        variant: "success",
      });
    } catch (err) {
      await showAlert({
        message:
          language === "ar"
            ? `فشل الحفظ: ${err.message}`
            : `Failed to save: ${err.message}`,
        variant: "error",
      });
    }
  };

  const handleAddEmployeeSuccess = (data) => {
    console.log("Employee added successfully:", data);
    // Refresh employee list via hook instead of full page reload
    try {
      // A new employee was just created, force a real refetch.
      if (typeof refetchEmployees === "function") {
        refetchEmployees(true);
      }
    } catch (e) {
      console.error("Failed to refetch employees after add:", e);
      // fallback to full reload if necessary
      window.location.reload();
    }
    // close modal
    setShowAddEmployee(false);
  };

  const handleAddEmployee = async (employeeData) => {
    try {
      setLoading(true);

      // ──────────────────────────────────────────────
      // Sanitize empty strings and invalid values
      // ──────────────────────────────────────────────
      const sanitize = (data) => {
        const copy = { ...(data || {}) };
        Object.keys(copy).forEach((key) => {
          if (copy[key] === "" || copy[key] === undefined) {
            copy[key] = null;
          }
        });
        return copy;
      };

      const sanitized = sanitize(employeeData);

      // ──────────────────────────────────────────────
      // Resolve client information from the profile or the form
      // ──────────────────────────────────────────────
      const resolvedClientName =
        profile?.clientName ||
        sanitized.workingCompany ||
        sanitized.company ||
        (companies && companies[0]) ||
        null;

      // Client Code is the employee's own code within the client — only what
      // the admin typed in the form. Left blank, the backend falls back to the
      // employee's auto-generated Infinity Code.
      const typedClientCode = String(sanitized.clientCode ?? "").trim();

      // Add client info to data
      sanitized.clientName = resolvedClientName;
      sanitized.clientCode = typedClientCode || null;

      console.log("📤 Form data before mapping:", sanitized);

      // ──────────────────────────────────────────────
      // Map form fields to database schema and apply transformations
      // ──────────────────────────────────────────────
      const payload = mapFormDataToDatabase(sanitized);

      console.log("📤 Database payload after mapping:", payload);

      // ──────────────────────────────────────────────
      // Call API and handle response
      // ──────────────────────────────────────────────
      const data = await addEmployeeAPI(sanitized);

      console.log("✅ Employee added successfully:", data);
      handleAddEmployeeSuccess(data);

      // Show success alert
      setAlertState({
        open: true,
        title: text[language].employeeAdded || "Success",
        message:
          text[language].employeeAddedMessage || "Employee added successfully",
        variant: "success",
        showCancel: false,
      });

      return data;
    } catch (err) {
      console.error("❌ Error adding employee:", err);

      const errorMessage =
        err.message || text[language].failedToAdd || "Failed to add employee";

      setAlertState({
        open: true,
        title: text[language].failedToAdd || "Error",
        message: errorMessage,
        variant: "error",
        showCancel: false,
      });

      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Cards read as "others + you". This list is what the admin level may see,
  // which usually includes you; when it does, CountWithSelf shows count − 1
  // so you are not counted twice. Plain numbers until the list has loaded, and
  // for accounts with no employee record.
  const isEmployeeViewer =
    (hookEmployees?.length ?? 0) > 0 &&
    !!profile?.infinityCode &&
    !!(profile?.employeeName || profile?.employeeState);
  const selfIncluded =
    isEmployeeViewer && isSelfInList(hookEmployees, profile.infinityCode);
  const withYou = (count, applies) => (
    <CountWithSelf
      count={count}
      show={isEmployeeViewer && applies}
      included={selfIncluded}
      label={text[language].you}
      hint={text[language].notInCount}
    />
  );

  // Stats data for StatsGrid
  const stats = [
    {
      title: text[language].totalEmployees,
      value: withYou(employeeCount, true),
      icon: icons.Users,
      iconBackground: "rgb(0, 180, 72)",
      showSymbol: true,
      valueAlign: 50,
      //subtitle: language === "ar" ? "الموظفين النشطين" : "Active staff",
    },
    // Active/On Leave are derived from the Status column, omit them
    // entirely when that column is hidden for this admin level, instead of
    // showing counts that don't reflect reality.
    ...(isStatusColumnVisible
      ? [
          {
            title: text[language].activeEmployees,
            value: withYou(counts.Active, profile?.employeeState === "Active"),
            description: text[language].thisWeek,
            icon: icons.UserCheck,
            iconBackground: "rgb(6, 0, 180)",
            valueAlign: 50,
          //  subtitle: language === "ar" ? "هذا الأسبوع" : "This week",
          },
          {
            title: text[language].onLeave,
            value: withYou(counts.OnLeave, profile?.employeeState === "OnLeave"),
            description: text[language].today,
            icon: icons.Calendar,
            iconBackground: "rgb(180, 0, 72)",
            valueAlign: 50,
           // subtitle: language === "ar" ? "اليوم" : "Today",
          },
        ]
      : []),
    {
      title: text[language].newEmployees,
      value: counts.newEmployees,
      description: text[language].ovearall,
      icon: icons.UserPlus,
      iconBackground: "rgb(180, 57, 0)",
      valueAlign: 50,
      //subtitle: language === "ar" ? "بشكل عام" : "Overall",
    },
    {
      title: text[language].archived,
      value: counts.archived,
      description: text[language].ovearall,
      icon: icons.Building,
      iconBackground: "rgb(96, 96, 96)",
      valueAlign: 50,
     // subtitle: language === "ar" ? "بشكل عام" : "Overall",
    },
  ];

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON, only until the first successful load
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && employeesLoading && canViewEmployees) {
    const isRTL = language === "ar";
    return (
      <div className="flex flex-col h-full">
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل بيانات الموظفين..." : "Loading employees information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Welcome card skeleton, welcomeCard carries a large negative
            marginTop meant to tuck it under the real topbar; neutralize it
            here so it doesn't slide up over the LoadingPill above it. */}
        <div style={{ ...styles.welcomeCard, marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <SkeletonBox width="240px" height="32px" style={{ marginBottom: "12px" }} />
            <SkeletonBox width="320px" height="18px" />
          </div>
        </div>

        {/* Stats grid skeleton */}
        <div style={styles.statsGrid}>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonStatCard key={i} cardStyle={{ ...styles.statCard, marginBottom: 0 }} />
          ))}
        </div>

        {/* Action buttons skeleton */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: styles?.statsGrid?.marginBottom || "24px", marginBottom: "20px" }}>
          {[1, 2, 3, 4].map((i) => (
            <SkeletonBox key={i} width="140px" height="42px" style={{ borderRadius: "10px" }} />
          ))}
        </div>

        {/* Filters + employee table skeleton */}
        <div style={{ ...styles.statCard, cursor: "default" }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "20px" }}>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonBox key={i} width="180px" height="42px" style={{ borderRadius: "10px" }} />
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <SkeletonBox width="100%" height="44px" style={{ borderRadius: "8px" }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonBox key={i} width="100%" height="56px" style={{ borderRadius: "8px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {!canViewEmployees ? (
        <div style={{ padding: "2rem", minHeight: "400px" }}>
          <EmptyCard
            styles={styles}
            hideTitle={true}
            actionsAlign="center"
            containerStyle={{ padding: "2rem" }}
          >
            <div style={{ textAlign: "center" }}>
              <h2 style={{ fontSize: "1.25rem", marginBottom: "0.75rem" }}>
                {text[language]?.accessDenied || "Access denied"}
              </h2>
              <p style={{ color: "#6b7280" }}>
                {text[language]?.noEmployeePermission ||
                  "You do not have permission to view employee management."}
              </p>
            </div>
          </EmptyCard>
        </div>
      ) : (
        <>
          {/* Welcome Section (use WelcomeCard for consistency) */}
          <WelcomeCard
            styles={styles}
            text={text}
            language={language}
            userName={getProfileFirstName(profile, language)}
            title={{ en: text.en.title, ar: text.ar.title }}
            subtitle={language === "ar" ? "إدارة وتنظيم ومتابعة الموظفين" : "Manage, organize, and track your workforce efficiently."}
          />

          <StatsGrid
            stats={stats}
            styles={styles}
            screenSize={screenSize}
            layout="inline"
            gridStyle={{
              // Fluid: one row of 5 on desktop (unchanged), wraps to 2-up /
              // 1-up on phones instead of clipping cards off-screen.
              gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
            }}
          />




          {/* Action Buttons */}
          {hasAdminActionButtons && (
            <div style={{ display: "none" }}>
              {/* We define the actionButtons inline as a prop to EmployeeTable */}
            </div>
          )}

          {/* Employee Table */}
          <div style={{ marginTop: "64px" }}>
            <EmployeeTable
              employees={hookEmployees}
              loading={employeesLoading}
              error={employeesError}
              onRetry={() => refetchEmployees(true)}
              clientsList={clientsList}
              companies={companies}
              onViewEmployee={handleViewEmployee}
              onEditEmployee={handleEditEmployee}
              onDeleteEmployee={handleDeleteEmployee}
              onViewDocuments={handleViewDocuments}
              onFilteredChange={setFilteredEmployeesForExport}
              canEditEmployee={canEditEmployee}
              canDeleteEmployee={canDeleteEmployee}
              canViewDocuments={isAdminRole || canViewDocuments}
              actionButtons={
                hasAdminActionButtons ? (
                  <div
                    className="nx-actions-row"
                    style={{
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                      paddingTop: "16px",
                      borderTop:
                        theme === "dark"
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid rgba(0,0,0,0.1)",
                      flexDirection: language === "ar" ? "row" : "row",
                    }}
                  >
                    {canCreateEmployee && (
                      <button
                        onClick={handleOpenAdd}
                        style={{
                          ...getTopBarButtonStyle(theme, false),
                          flexDirection: language === "ar" ? "row-reverse" : "row",
                          background: "#2e7cba",
                          color: "#fff",
                          border: "none",
                        }}
                      >
                        <Plus size={16} />
                        {text[language].addNewEmployee}
                      </button>
                    )}

                    {canImportEmployees && (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls,.csv"
                          style={{ display: "none" }}
                          onChange={handleImportExcel}
                          disabled={loading || isPreparingImport}
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={loading || isPreparingImport}
                          title={
                            language === "ar"
                              ? "استيراد الموظفين من ملف إكسل"
                              : "Import employees from an Excel file"
                          }
                          style={{
                            ...getTopBarButtonStyle(theme, loading || isPreparingImport),
                            flexDirection: language === "ar" ? "row-reverse" : "row",
                          }}
                          onMouseEnter={(e) => {
                            if (!(loading || isPreparingImport)) {
                              e.currentTarget.style.background = theme === "dark"
                                ? "rgba(30, 41, 59, 1)"
                                : "rgba(51, 65, 85, 0.2)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = theme === "dark"
                              ? "rgba(30, 41, 59, 0.8)"
                              : "rgba(51, 65, 85, 0.12)";
                          }}
                        >
                          <Upload size={16} />
                          {isPreparingImport
                            ? text[language].processingImport || "Processing..."
                            : text[language].import}
                        </button>
                      </>
                    )}

                    {canExportEmployees && (
                      <button
                        onClick={handleExportToExcel}
                        disabled={loading || isExporting}
                        style={{
                          ...getTopBarButtonStyle(theme, loading || isExporting),
                          flexDirection: language === "ar" ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          if (!(loading || isExporting)) {
                            e.currentTarget.style.background = theme === "dark"
                              ? "rgba(30, 41, 59, 1)"
                              : "rgba(51, 65, 85, 0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <Download size={16} />
                        {isExporting
                          ? text[language].exporting || "Exporting..."
                          : text[language].export}
                      </button>
                    )}

                    {(isAdminRole || canViewArchivedEmployees) && (
                      <button
                        onClick={handleOpenArchive}
                        disabled={loading || archiveLoading}
                        style={{
                          ...getTopBarButtonStyle(theme, loading || archiveLoading),
                          flexDirection: language === "ar" ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          if (!(loading || archiveLoading)) {
                            e.currentTarget.style.background = theme === "dark"
                              ? "rgba(30, 41, 59, 1)"
                              : "rgba(51, 65, 85, 0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <Archive size={16} />
                        {`${text[language].archived}`}
                      </button>
                    )}

                    {(isAdminRole || canExportTemplate) && (
                      <button
                        onClick={handleOpenExportTemplate}
                        disabled={loading}
                        style={{
                          ...getTopBarButtonStyle(theme, loading),
                          flexDirection: language === "ar" ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.background = theme === "dark"
                              ? "rgba(30, 41, 59, 1)"
                              : "rgba(51, 65, 85, 0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <FileText size={16} />
                        {text[language].exportTemplate}
                      </button>
                    )}

                    {isAdminOnlyRole && (
                      <button
                        onClick={() => setShowHeadcountVerify(true)}
                        disabled={loading}
                        title={
                          language === "ar"
                            ? "مقارنة ملف إكسل بأكواد الموظفين بالموظفين النشطين في النظام"
                            : "Compare an Excel list of client codes with the active employees in the system"
                        }
                        style={{
                          ...getTopBarButtonStyle(theme, loading),
                          flexDirection: language === "ar" ? "row-reverse" : "row",
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.background = theme === "dark"
                              ? "rgba(30, 41, 59, 1)"
                              : "rgba(51, 65, 85, 0.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(30, 41, 59, 0.8)"
                            : "rgba(51, 65, 85, 0.12)";
                        }}
                      >
                        <ShieldCheck size={16} />
                        {language === "ar" ? "التحقق من عدد الموظفين" : "Verify Headcount"}
                      </button>
                    )}
                  </div>
                ) : null
              }
            />
          </div>
          <AlertModal
            open={alertState.open}
            title={alertState.title}
            message={alertState.message}
            variant={alertState.variant}
            showCancel={alertState.showCancel}
            onConfirm={() => {
              alertState.onResolve?.(true);
              setAlertState((s) => ({ ...s, open: false }));
            }}
            onClose={() => {
              alertState.onResolve?.(false);
              setAlertState((s) => ({ ...s, open: false }));
            }}
            theme={theme}
            language={language}
          />

          {/* View Employee Modal */}
          {showViewEmployee && selectedEmployeeForView && (
            <ViewEmployeeModal
              selectedEmployee={selectedEmployeeForView}
              setShowViewEmployee={setShowViewEmployee}
              text={text}
              language={language}
              screenSize={screenSize}
              spacing={{ margin: 12 }}
              employeeProfilePics={employeeProfilePics}
              getUserInitials={getUserInitials}
              getStatusBadge={getStatusBadge}
              theme={theme}
            />
          )}

          {/* Edit Employee Modal */}
          {showEditEmployee && selectedEmployee && (
            <EditEmployeeModal
              selectedEmployee={selectedEmployee}
              setShowEditEmployee={setShowEditEmployee}
              setSelectedEmployee={setSelectedEmployee}
              language={language}
              text={text}
              unifiedControlStyle={unifiedControlStyle}
              departments={departmentsList}
              ReusableDropdown={ReusableDropdown}
              handleUpdateEmployee={handleUpdateEmployee}
              handleDeleteEmployee={handleDeleteEmployee}
              clientName={profile?.clientName}
              companies={companies}
              theme={theme}
              screenSize={screenSize}
              loading={loading}
            />
          )}

          {/* Documents Modal */}
          {showDocumentsModal && (
            <DocumentsModal
              selectedEmployeeDocuments={selectedEmployeeDocuments}
              setShowDocumentsModal={setShowDocumentsModal}
              setSelectedEmployeeDocuments={setSelectedEmployeeDocuments}
              apiFetch={apiFetch}
              API_URL={BASE_URL}
              showAlert={showAlert}
              language={language}
              screenSize={screenSize}
              text={text}
              loadingDocuments={loadingDocuments}
              theme={theme}
              spacing={{ margin: "1.5rem", gap: "1rem", padding: "1rem" }}
              fonts={{ body: "14px", small: "12px", subtitle: "16px" }}
              documentChecklist={documentChecklist}
              handleDocumentCheckboxChange={updateDocumentCheckbox}
              handleSaveDocuments={handleSaveDocuments}
              hasUnsavedChanges={hasUnsavedChanges}
              savingDocument={savingDocument}
            />
          )}

          {showAddEmployee && (
            <AddEmployeeModal
              styles={styles}
              departments={departmentsList}
              companies={companies}
              departmentsLoading={employeesLoading}
              loading={loading}
              screenSize={screenSize}
              onClose={() => setShowAddEmployee(false)}
              onSubmit={handleAddEmployee}
              clientName={profile?.clientName}
              quotaStatus={importQuotaStatus}
              quotaLoading={importQuotaLoading}
            />
          )}

          {/* Import Preview Modal */}
          {importPreviewOpen && (
            <ImportPreviewModal
              importPreviewData={importPreviewData}
              onClose={closeImportPreview}
              pendingImportFile={pendingImportFile}
              duplicateDecisions={duplicateDecisions}
              setDuplicateDecisions={setDuplicateDecisions}
              managerResolutions={managerResolutions}
              setManagerResolutions={setManagerResolutions}
              previewStep={previewStep}
              setPreviewStep={setPreviewStep}
              expandedDupKeys={expandedDupKeys}
              setExpandedDupKeys={setExpandedDupKeys}
              isImportCommitting={isImportCommitting}
              isPreparingImport={isPreparingImport}
              commitImportWithDecisions={commitImportWithDecisions}
              theme={theme}
              language={language}
              screenSize={screenSize}
              text={text}
              showAlert={showAlert}
            />
          )}

          {/* Import Results Modal */}
          {importResultsOpen && (
            <ImportResultsModal
              importResultsData={importResultsData}
              setImportResultsOpen={setImportResultsOpen}
              styles={styles}
              fonts={{ body: "1rem", small: "0.875rem", tiny: "0.8125rem" }}
              theme={theme}
              language={language}
              screenSize={screenSize}
            />
          )}

          {/* Import Progress Widget (floating, minimizable, closable) */}
          <ImportProgressWidget
            visible={importProgress.visible}
            minimized={importProgress.minimized}
            percent={importProgress.percent}
            etaSeconds={importProgress.etaSeconds}
            fileName={importProgress.fileName}
            language={language}
            theme={theme}
            onMinimize={handleMinimizeImportProgress}
            onExpand={handleExpandImportProgress}
            onClose={handleCloseImportProgress}
          />

          {/* Archive Modal */}
          {showArchive && (
            <ArchiveModal
              archivedEmployees={archivedEmployees}
              archivedCount={archivedCount}
              setShowArchive={setShowArchive}
              archiveSearch={archiveSearch}
              setArchiveSearch={setArchiveSearch}
              archiveFilterCompany={archiveFilterCompany}
              setArchiveFilterCompany={setArchiveFilterCompany}
              archiveFilterCompanySearch={archiveFilterCompanySearch}
              setArchiveFilterCompanySearch={setArchiveFilterCompanySearch}
              archiveFilterWorkingCompany={archiveFilterWorkingCompany}
              setArchiveFilterWorkingCompany={setArchiveFilterWorkingCompany}
              archiveFilterWorkingCompanySearch={archiveFilterWorkingCompanySearch}
              setArchiveFilterWorkingCompanySearch={
                setArchiveFilterWorkingCompanySearch
              }
              archiveFilterDepartment={archiveFilterDepartment}
              setArchiveFilterDepartment={setArchiveFilterDepartment}
              archiveFilterDepartmentSearch={archiveFilterDepartmentSearch}
              setArchiveFilterDepartmentSearch={setArchiveFilterDepartmentSearch}
              archiveFilterDateFrom={archiveFilterDateFrom}
              setArchiveFilterDateFrom={setArchiveFilterDateFrom}
              archiveFilterDateTo={archiveFilterDateTo}
              setArchiveFilterDateTo={setArchiveFilterDateTo}
              filteredArchivedEmployees={filteredArchivedEmployees}
              filterOptions={filterOptions}
              archivedThisMonth={archivedThisMonth}
              // Gates the "Export Archive" button, same "export" feature as
              // the main export button (admins bypass via isAdminRole).
              isAdminRole={isAdminRole || canExportEmployees}
              loading={loading || archiveLoading}
              handleExportArchive={handleExportArchive}
              handleRestoreEmployee={handleRestoreEmployee}
              handleDeleteEmployee={handleDeleteArchivedEmployee}
              handleRestoreAllArchivedEmployees={handleRestoreAllArchivedEmployees}
              handleDeleteAllArchived={handleDeleteAllArchived}
              clearArchiveFilters={clearArchiveFilters}
              getStatusBadge={getStatusBadge}
              theme={theme}
              language={language}
              screenSize={screenSize}
              text={text}
              showAlert={showAlert}
              showConfirm={showConfirm}
              ReusableDropdown={ReusableDropdown}
              // "deleteArchived" gates the archive row Delete (independent of
              // "remove", which only controls deleting from the live list);
              // "restoreArchived" gates row Restore + Restore All (admins
              // bypass both via isAdminRole).
              canDeleteEmployee={isAdminRole || canDeleteArchivedEmployee}
              canRestoreEmployee={isAdminRole || canRestoreArchived}
            />
          )}

          {/* Headcount Verification Modal (admin role only) */}
          {isAdminOnlyRole && (
            <HeadcountVerifyModal
              isOpen={showHeadcountVerify}
              onClose={() => {
                setShowHeadcountVerify(false);
                // Actions in the modal may have changed employees: refresh the table.
                if (typeof refetchEmployees === "function") refetchEmployees(true);
              }}
              companyOptions={headcountCompanyOptions}
              theme={theme}
              language={language}
              hidden={headcountSuspended}
              refreshSignal={headcountRefresh}
              onEditEmployee={handleHeadcountEdit}
              onViewEmployee={handleHeadcountView}
              onAddEmployee={handleHeadcountAdd}
            />
          )}

          {/* Export Template Modal */}
          <ExportTemplateModal
            isOpen={showExportTemplate}
            onClose={() => setShowExportTemplate(false)}
            fileName={(mode) => (mode === "net" ? "employee-template-net.xlsx" : "employee-template-gross.xlsx")}
            title={text[language].exportTemplate}
            description={
              language === "ar"
                ? "تحميل قالب الموظفين للاستيراد الجماعي"
                : "Download the employee template for bulk import operations"
            }
            optionsTitle={
              language === "ar"
                ? "أي راتب ستقوم بإدخاله في القالب؟"
                : "Which salary will you type into the template?"
            }
            options={[
              {
                key: "gross",
                label: language === "ar" ? "الراتب الإجمالي (Gross)" : "Gross Salary",
                description:
                  language === "ar"
                    ? "تكتب الإجمالي ويُحسب الصافي تلقائياً"
                    : "You type Gross, Net is calculated automatically",
              },
              {
                key: "net",
                label: language === "ar" ? "الراتب الصافي (Net)" : "Net Salary",
                description:
                  language === "ar"
                    ? "تكتب الصافي ويُحسب الإجمالي تلقائياً"
                    : "You type Net, Gross is calculated automatically",
              },
            ]}
            defaultOption="gross"
            successMessage={text[language].templateExported}
            theme={theme}
            language={language}
            screenSize={screenSize}
            isLoading={exportTemplateLoading}
            onExport={handleExportTemplate}
          />
        </>
      )}
    </div>
  );
};

export default Dashboard;
